import { Position } from '../domain/Position';
import { TerritoryState } from '../domain/Territory';
/**
 * Movement Validator
 * Валидирует ходы согласно правилам Adhesion
 */
export class MovementValidator {
    board;
    constructor(board) {
        this.board = board;
    }
    /**
     * Проверяет валидность хода
     */
    validateMove(sideId, from, to) {
        const result = {
            isValid: false,
            from,
            to,
            sideId,
        };
        // 1. Проверка существования стороны
        const side = this.board.getSide(sideId);
        if (!side) {
            result.reason = `Side ${sideId} does not exist`;
            return result;
        }
        // 2. Проверка активности стороны
        if (!side.isActive) {
            result.reason = `Side ${sideId} is not active`;
            return result;
        }
        // 3. Проверка текущего хода
        if (this.board.currentSide !== sideId) {
            result.reason = `It's not side ${sideId}'s turn (current: ${this.board.currentSide})`;
            return result;
        }
        // 4. Проверка исходной позиции как активного поля
        const fromKey = from.toKey();
        if (!side.turnActiveFields.has(fromKey)) {
            result.reason = `Position ${from} is not an active field for side ${sideId}`;
            return result;
        }
        // 4.1 Проверка, что активное поле все еще принадлежит стороне.
        // Это синхронизирует интерпретацию active fields с UI, где
        // подсвечиваются только поля владельца стороны.
        const fromTerritory = this.board.getTerritoryAt(from);
        if (!fromTerritory || fromTerritory.owner !== sideId) {
            result.reason = `Position ${from} is not owned by side ${sideId}`;
            return result;
        }
        // 5. Проверка валидности хода конём
        if (!this.isValidKnightMove(from, to)) {
            result.reason = `Invalid knight move from ${from} to ${to}`;
            return result;
        }
        // 6. Проверка целевой территории
        const targetTerritory = this.board.getTerritoryAt(to);
        if (!targetTerritory) {
            result.reason = `Target position ${to} does not exist`;
            return result;
        }
        // 7. Проверка доступности территории
        if (!targetTerritory.isAvailableForMovement(sideId)) {
            result.reason = `Territory at ${to} is not available for side ${sideId} (state: ${targetTerritory.state}, owner: ${targetTerritory.owner}, occupied: ${targetTerritory.occupiedBy})`;
            return result;
        }
        result.isValid = true;
        return result;
    }
    /**
     * Проверяет, является ли ход валидным ходом конём
     */
    isValidKnightMove(from, to) {
        const dx = Math.abs(to.x - from.x);
        const dy = Math.abs(to.y - from.y);
        // Ход конём: (2,1) или (1,2)
        return (dx === 2 && dy === 1) || (dx === 1 && dy === 2);
    }
    /**
     * Получает все доступные ходы для стороны
     */
    getAvailableMovesForSide(sideId) {
        const moves = new Map();
        const side = this.board.getSide(sideId);
        if (!side || !side.isActive) {
            return moves;
        }
        for (const activeFieldKey of side.turnActiveFields) {
            const from = Position.fromKey(activeFieldKey);
            const availableMoves = this.getAvailableMovesFrom(from, sideId);
            if (availableMoves.length > 0) {
                moves.set(activeFieldKey, availableMoves);
            }
        }
        return moves;
    }
    /**
     * Получает доступные ходы с конкретной позиции
     * @param from - Позиция откуда ходить
     * @param sideId - ID стороны (опционально, для проверки возможности хода на свои территории)
     */
    getAvailableMovesFrom(from, sideId) {
        const potentialMoves = from.getKnightMoves();
        const availableMoves = [];
        for (const to of potentialMoves) {
            const territory = this.board.getTerritoryAt(to);
            if (territory?.isAvailableForMovement(sideId)) {
                availableMoves.push(to);
            }
        }
        return availableMoves;
    }
    /**
     * Проверяет, есть ли доступные ходы у стороны
     */
    hasAvailableMoves(sideId) {
        const side = this.board.getSide(sideId);
        if (!side || !side.isActive) {
            return false;
        }
        // 🔍 ДИАГНОСТИКА: Детальная проверка каждой позиции
        console.log(`[hasAvailableMoves] Side ${sideId}: checking ${side.turnActiveFields.size} turnActiveFields`);
        for (const activeFieldKey of side.turnActiveFields) {
            const from = Position.fromKey(activeFieldKey);
            const moves = this.getAvailableMovesFrom(from, sideId);
            if (moves.length > 0) {
                console.log(`[hasAvailableMoves] Side ${sideId}: found ${moves.length} moves from ${activeFieldKey} -> ${moves.map(m => m.toKey()).join(', ')}`);
                // 🔍 ДЕТАЛЬНАЯ ПРОВЕРКА каждого хода
                for (const move of moves) {
                    const territory = this.board.getTerritoryAt(move);
                    console.log(`[hasAvailableMoves]   Target ${move.toKey()}: state=${territory?.state}, owner=${territory?.owner}, occupiedBy=${territory?.occupiedBy}`);
                }
                return true;
            }
        }
        console.log(`[hasAvailableMoves] Side ${sideId}: NO available moves found`);
        return false;
    }
    /**
     * Проверяет, есть ли доступные ходы с конкретной позиции для стороны
     */
    hasAvailableMovesFromPosition(position, sideId) {
        const side = this.board.getSide(sideId);
        if (!side || (!side.isActive && !side.isSuspended)) {
            return false;
        }
        // Проверяем что позиция принадлежит этой стороне
        const territory = this.board.getTerritoryAt(position);
        if (!territory || territory.owner !== sideId) {
            return false;
        }
        // Получаем доступные ходы конём с этой позиции
        const knightMoves = position.getKnightMoves();
        for (const move of knightMoves) {
            const targetTerritory = this.board.getTerritoryAt(move);
            // Проверяем что можно походить на эту территорию
            if (targetTerritory && targetTerritory.isAvailableForMovement(sideId)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Проверяет, не приведёт ли ход к попаданию в ловушку
     * ВАЖНО: Ловушка срабатывает когда ВСЕ 4 ортогональные соседа заняты ДРУГИМИ сторонами
     * (не нейтральные и не свои собственные)
     * Нейтральные территории (owner=null) БЛОКИРУЮТ ловушку
     * Свои территории (owner === sideId) БЛОКИРУЮТ ловушку
     * Союзники (другие стороны того же игрока) СОЗДАЮТ ловушку!
     */
    checkForTrap(to, sideId) {
        const neighbors = to.getOrthogonalNeighbors();
        let blockingCount = 0;
        const neighborDetails = [];
        // Проверяем каждого соседа
        for (const neighborPos of neighbors) {
            const neighbor = this.board.getTerritoryAt(neighborPos);
            // Ловушка создаётся если территория:
            // 1. Существует
            // 2. Имеет владельца (не нейтральная)
            // 3. НЕ принадлежит текущей стороне (союзники и враги - создают ловушку!)
            if (neighbor && neighbor.state === TerritoryState.BURNED) {
                // Burned land acts like a hard wall for trap checks.
                blockingCount++;
                neighborDetails.push(`${neighborPos.toKey()}:BURNED[TRAP]`);
            }
            else if (neighbor && neighbor.owner !== null && neighbor.owner !== sideId) {
                blockingCount++;
                neighborDetails.push(`${neighborPos.toKey()}:owner=${neighbor.owner}[TRAP]`);
            }
            else if (!neighbor) {
                // Край доски - считается как блокировка
                blockingCount++;
                neighborDetails.push(`${neighborPos.toKey()}:EDGE[BLOCKS]`);
            }
            else if (neighbor.owner === null) {
                neighborDetails.push(`${neighborPos.toKey()}:NEUTRAL[BLOCKS]`);
            }
            else if (neighbor.owner === sideId) {
                neighborDetails.push(`${neighborPos.toKey()}:OWN[BLOCKS]`);
            }
        }
        const isTrapped = blockingCount === neighbors.length;
        // ДИАГНОСТИКА: Логируем проверку ловушки
        console.log(`[TRAP-CHECK] Position ${to.toKey()} for side ${sideId}: ${isTrapped ? 'TRAPPED!' : 'NOT trapped'}`);
        console.log(`[TRAP-CHECK]   Neighbors (${neighbors.length}): ${neighborDetails.join(', ')}`);
        console.log(`[TRAP-CHECK]   Blocking count: ${blockingCount}/${neighbors.length}`);
        // Ловушка срабатывает если ВСЕ 4 соседа принадлежат ДРУГИМ сторонам
        // (не нейтральные, не свои)
        // Союзники тоже создают ловушку!
        return isTrapped;
    }
    /**
     * Получает информацию о потенциальной ловушке
     * surroundingSides включает ВСЕ другие стороны (и врагов, и союзников)
     */
    getTrapInfo(position, sideId, currentRound = 1, maxMovesPerRound = 3) {
        const neighbors = position.getOrthogonalNeighbors();
        const surroundingSides = new Set();
        for (const neighborPos of neighbors) {
            const neighbor = this.board.getTerritoryAt(neighborPos);
            // Собираем ВСЕ стороны кроме своей (и врагов, и союзников!)
            // Союзники тоже участвуют в ловушке и расчёте силы
            if (neighbor && neighbor.owner !== null && neighbor.owner !== sideId) {
                surroundingSides.add(neighbor.owner);
            }
        }
        const isTrapped = this.checkForTrap(position, sideId);
        if (!isTrapped) {
            return {
                isTrapped: false,
                surroundingSides: Array.from(surroundingSides),
                canEscape: false,
            };
        }
        const side = this.board.getSide(sideId);
        if (!side) {
            return {
                isTrapped,
                surroundingSides: Array.from(surroundingSides),
                canEscape: false,
            };
        }
        // Определяем тип ловушки
        let trapType = 'force';
        let canEscape = false;
        // surroundingSides содержит ВСЕ другие стороны (враги + союзники)
        // Ситуация III: Монопольное окружение (все 4 соседа принадлежат ОДНОЙ стороне)
        // Это работает и для врагов, и для союзников!
        if (surroundingSides.size === 1) {
            trapType = 'monopoly';
            canEscape = false;
        }
        // Ситуация I или II: Несколько сторон окружают
        else {
            const remainingMoves = side.getRemainingMoves(currentRound, maxMovesPerRound);
            // ИСПРАВЛЕНИЕ: getTrapInfo() вызывается ДО makeMove(), поэтому remainingMoves
            // ещё не учитывает текущий ход в ловушку. Нужно проверять > 1, а не > 0.
            // Если remainingMoves = 1, этот единственный ход и есть текущий ход в ловушку.
            if (remainingMoves > 1 && this.hasAvailableMovesFromOtherPositions(sideId)) {
                trapType = 'escapable';
                canEscape = true;
            }
            // Ситуация II: Нет ходов, решается по силе присутствия
            else {
                trapType = 'force';
                canEscape = false;
            }
        }
        return {
            isTrapped,
            surroundingSides: Array.from(surroundingSides),
            canEscape,
            trapType,
        };
    }
    /**
     * Проверяет, есть ли доступные ходы с других позиций
     */
    hasAvailableMovesFromOtherPositions(sideId) {
        const side = this.board.getSide(sideId);
        if (!side)
            return false;
        // Проверяем каждое активное поле
        for (const activeFieldKey of side.activeFields) {
            const activePos = Position.fromKey(activeFieldKey);
            const availableMoves = this.getAvailableMovesFrom(activePos);
            if (availableMoves.length > 0) {
                return true;
            }
        }
        return false;
    }
}
//# sourceMappingURL=MovementValidator.js.map